import { Link, useLocation } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, MapPin, ChevronDown } from "lucide-react";
import { MaskedLines } from "../Reveal";
import { scrollToEl } from "../../lib/smooth";
import { useContents } from "../../hooks/useContents";
import { imgUrl } from "../../lib/api";

export const Hero = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 700], [0, 180]);
  const location = useLocation();
  const c = useContents();

  const goQuote = (e) => {
    if (location.pathname === "/") {
      e.preventDefault();
      scrollToEl("#preventivo");
    }
  };

  return (
    <section data-testid="hero-section" className="relative min-h-[100svh] flex items-end overflow-hidden">
      <motion.div style={{ y }} className="absolute inset-0 -bottom-48" aria-hidden="true">
        <img src={imgUrl(c("hero_img", "/images/hero.jpg"))} alt="" className="w-full h-full object-cover" />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-t from-base via-base/60 to-base/40" aria-hidden="true" />
      <div className="absolute inset-0 bg-gradient-to-r from-base/80 via-transparent to-transparent" aria-hidden="true" />

      <div className="relative w-full max-w-7xl mx-auto px-5 lg:px-10 pb-24 pt-40">
        <motion.div
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.7 }}
          className="flex items-center gap-3"
        >
          <span className="inline-block w-10 h-px bg-forest-glow" aria-hidden="true" />
          <p className="overline-label">Impresa edile — Veneto</p>
        </motion.div>

        <h1 className="h-display text-4xl sm:text-5xl lg:text-7xl mt-7 text-white max-w-5xl">
          <MaskedLines
            onLoad
            baseDelay={0.35}
            lines={[
              <>{c("hero_line1", "Costruiamo solido.")}</>,
              <span className="text-forest-glow">{c("hero_line2", "Costruiamo bene.")}</span>,
            ]}
          />
        </h1>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          className="mt-8 max-w-2xl"
        >
          <p className="text-metal text-base sm:text-lg font-medium">
            {c("hero_subtitle", "Costruzioni Ciosko — professionalità, esperienza e passione per l'edilizia.")}
          </p>
          <p className="text-cement text-sm sm:text-base mt-3 leading-relaxed">
            {c("hero_text", "Dalle opere murarie alle ristrutturazioni, realizziamo ogni intervento con attenzione ai dettagli, organizzazione e rispetto delle tempistiche.")}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.1, duration: 0.8 }}
          className="mt-9 flex flex-col sm:flex-row gap-3"
        >
          <Link to="/contatti" onClick={goQuote} data-testid="hero-quote-button" className="btn-primary">
            Richiedi un preventivo <ArrowRight size={16} />
          </Link>
          <button onClick={() => scrollToEl("#servizi")} data-testid="hero-services-button" className="btn-ghost">
            Scopri i nostri servizi
          </button>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.35, duration: 0.8 }}
          className="mt-10 flex items-center gap-2.5 text-cement"
        >
          <MapPin size={15} className="text-forest-glow" />
          <span className="font-mono text-[11px] uppercase tracking-[0.28em]">Operativi in Veneto</span>
        </motion.div>
      </div>

      <motion.button
        onClick={() => scrollToEl("#chi-siamo")}
        aria-label="Scorri alla sezione successiva"
        data-testid="hero-scroll-indicator"
        className="absolute bottom-6 right-6 hidden md:flex w-11 h-11 items-center justify-center border border-steel text-cement hover:text-forest-glow hover:border-forest-glow transition-colors"
        animate={{ y: [0, 6, 0] }}
        transition={{ repeat: Infinity, duration: 2.2, ease: "easeInOut" }}
      >
        <ChevronDown size={18} />
      </motion.button>
    </section>
  );
};
